import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  initialViewport,
  zoomAt,
  panBy,
  clampCropRect,
  getSliceGridLines,
  sourceRectToScreen,
} from '../../lib/cropViewport';
import { suggestCrop, assertSliceCount } from '../../lib/geometry';
import { nudgeCrop } from '../../lib/carousel';

// Presets per plan Step 4: "5 x 4:5", "6 x 4:5", "8 x 4:5", "5 x 1:1".
const PRESETS = [
  { label: '5 x 4:5', n: 5, aspect: '4:5' },
  { label: '6 x 4:5', n: 6, aspect: '4:5' },
  { label: '8 x 4:5', n: 8, aspect: '4:5' },
  { label: '5 x 1:1', n: 5, aspect: '1:1' },
];

const ASPECTS = ['4:5', '1:1', '16:9'];
const MIN_ZOOM_FACTOR = 0.05; // floor relative to fit scale
const MAX_ZOOM_FACTOR = 32;   // ceiling relative to fit scale

// Safe-zone box (top-right of each slice) representing the 1/N counter
// overlay — 10% of slice width, 8% of slice height, red, per plan Step 4.
const SAFE_ZONE_W = 0.10;
const SAFE_ZONE_H = 0.08;

const CropUI = ({ image, onCropChange }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [viewport, setViewport] = useState(null); // {scale, ox, oy}
  const [crop, setCrop] = useState(null);         // {x,y,width,height} source px
  const [n, setN] = useState(5);
  const [aspect, setAspect] = useState('4:5');
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState(null);

  const dragRef = useRef(null); // {mode:'crop'|'pan', startX, startY, startCrop, startViewport}

  // Suggest crop when image, n, or aspect changes.
  useEffect(() => {
    if (!image) return;

    const suggested = suggestCrop({
      srcWidth: image.width,
      srcHeight: image.height,
      n,
      targetSliceAspect: aspect,
    });

    if (!suggested) {
      setError(`Cannot fit ${n} slices at ${aspect} in a ${image.width}x${image.height} image. Choose fewer slices or a wider image.`);
      setCrop(null);
      setWarnings([]);

      return;
    }

    setError(null);
    setCrop(suggested.cropRect);
    setWarnings(suggested.warnings);
  }, [image, n, aspect]);

  // Initialize viewport to fit, and keep it clamped on size changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container || !image) return;
    const { clientWidth, clientHeight } = container;
    const vp = initialViewport(clientWidth, clientHeight, image.width, image.height);
    setViewport(vp);
  }, [image]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;

    if (!canvas || !image || !viewport || !crop) return;
    const ctx = canvas.getContext('2d');
    const { clientWidth, clientHeight } = canvas;
    // Backing store matches CSS size * devicePixelRatio for crisp lines.
    const dpr = window.devicePixelRatio || 1;

    if (canvas.width !== Math.round(clientWidth * dpr) || canvas.height !== Math.round(clientHeight * dpr)) {
      canvas.width = Math.round(clientWidth * dpr);
      canvas.height = Math.round(clientHeight * dpr);
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, clientWidth, clientHeight);

    // Background.
    ctx.fillStyle = '#222222';
    ctx.fillRect(0, 0, clientWidth, clientHeight);

    // Source image (the whole bitmap, drawn through the viewport).
    const imgW = image.width * viewport.scale;
    const imgH = image.height * viewport.scale;
    const imgX = -viewport.ox * viewport.scale;
    const imgY = -viewport.oy * viewport.scale;
    ctx.drawImage(image, imgX, imgY, imgW, imgH);

    if (!crop) return;

    const cropScreen = sourceRectToScreen(crop, viewport);

    // Dim everything outside the crop (the crop is the only part that ships).
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, clientWidth, clientHeight);
    ctx.clearRect(cropScreen.x, cropScreen.y, cropScreen.width, cropScreen.height);

    // Crop border.
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.strokeRect(cropScreen.x, cropScreen.y, cropScreen.width, cropScreen.height);

    // N-1 slice boundary lines (live-update as N changes).
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);

    for (const lineX of getSliceGridLines(crop, n)) {
      const sx = (lineX - viewport.ox) * viewport.scale;
      ctx.beginPath();
      ctx.moveTo(sx, cropScreen.y);
      ctx.lineTo(sx, cropScreen.y + cropScreen.height);
      ctx.stroke();
    }

    ctx.setLineDash([]);

    // Red safe-zone box in the top-right of each slice (1/N counter area).
    const sliceW = cropScreen.width / n;
    ctx.strokeStyle = '#ff3333';
    ctx.lineWidth = 2;

    for (let i = 0; i < n; i++) {
      const bx = cropScreen.x + i * sliceW + sliceW * (1 - SAFE_ZONE_W);
      const by = cropScreen.y;
      ctx.strokeRect(bx, by, sliceW * SAFE_ZONE_W, cropScreen.height * SAFE_ZONE_H);
    }
  }, [image, viewport, crop, n]);

  useEffect(() => {
    draw();
  }, [draw]);

  const onWheel = useCallback(
    (e) => {
      e.preventDefault();

      if (!viewport || !image) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const fit = Math.min(rect.width / image.width, rect.height / image.height);
      const factor = e.deltaY < 0 ? 1.2 : 1 / 1.2;
      setViewport((vp) =>
        zoomAt(vp, cx, cy, factor, fit * MIN_ZOOM_FACTOR, fit * MAX_ZOOM_FACTOR, rect.width, rect.height, image.width, image.height)
      );
    },
    [viewport, image]
  );

  const onPointerDown = useCallback(
    (e) => {
      if (!viewport || !crop || !image) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const cropScreen = sourceRectToScreen(crop, viewport);

      const insideCrop =
        px >= cropScreen.x && px <= cropScreen.x + cropScreen.width &&
        py >= cropScreen.y && py <= cropScreen.y + cropScreen.height;

      dragRef.current = {
        mode: insideCrop ? 'crop' : 'pan',
        startX: px,
        startY: py,
        startCrop: { ...crop },
        startViewport: { ...viewport },
      };
      canvasRef.current.setPointerCapture?.(e.pointerId);
    },
    [viewport, crop, image]
  );

  const onPointerMove = useCallback(
    (e) => {
      const drag = dragRef.current;

      if (!drag || !viewport || !image) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const dx = px - drag.startX;
      const dy = py - drag.startY;

      if (drag.mode === 'crop') {
        // Move the crop; clampCropRect enforces "crop cannot be dragged
        // outside source bounds" (plan Step 4 acceptance).
        const moved = {
          ...drag.startCrop,
          x: drag.startCrop.x + dx / viewport.scale,
          y: drag.startCrop.y + dy / viewport.scale,
        };

        setCrop(clampCropRect(moved, image.width, image.height));
      } else {
        setViewport(panBy(drag.startViewport, dx, dy, rect.width, rect.height, image.width, image.height));
      }
    },
    [viewport, image]
  );

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  // Red-team D4 (deferred to M6): keyboard crop positioning. Arrow keys
  // nudge the crop by 1px (10px with Shift); clamped to source bounds.
  const onKeyDown = useCallback(
    (e) => {
      if (!crop || !image) return;
      const step = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;

      if (e.key === 'ArrowLeft') dx = -1;
      else if (e.key === 'ArrowRight') dx = 1;
      else if (e.key === 'ArrowUp') dy = -1;
      else if (e.key === 'ArrowDown') dy = 1;
      else return;
      e.preventDefault();
      setCrop(nudgeCrop(crop, dx, dy, image.width, image.height, step));
    },
    [crop, image]
  );

  // Report crop changes upward for the pipeline (encode uses it later).
  useEffect(() => {
    if (crop) onCropChange?.(crop);
  }, [crop, onCropChange]);

  const applyPreset = (preset) => {
    setN(preset.n);
    setAspect(preset.aspect);
  };

  const onCustomN = (e) => {
    const value = Number(e.target.value);

    try {
      assertSliceCount(value);
      setN(value);
    } catch {
      // ignore out-of-range during typing; suggestCrop effect validates
    }
  };

  return (
    <div className="crop-ui">
      <div className="crop-controls">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className={preset.n === n && preset.aspect === aspect ? 'active' : ''}
            onClick={() => applyPreset(preset)}
          >
            {preset.label}
          </button>
        ))}
        <label>
          N
          <select value={n} onChange={onCustomN}>
            {[5, 6, 7, 8, 9, 10].map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          Aspect
          <select value={aspect} onChange={(e) => setAspect(e.target.value)}>
            {ASPECTS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="crop-error">{error}</p>}
      {warnings.map((w, i) => (
        <p key={i} className="crop-warning">{w}</p>
      ))}

      <div className="crop-canvas-container" ref={containerRef}>
        <canvas
          ref={canvasRef}
          tabIndex={0}
          aria-label="Crop preview. Drag inside the crop to position it, drag outside to pan, scroll to zoom, or use arrow keys to nudge the crop."
          onWheel={onWheel}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        />
        <p className="crop-hint">Drag inside the crop to position it; drag outside to pan; scroll to zoom.</p>
      </div>
    </div>
  );
};

export default CropUI;